import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import {
  ROLES_TAG_TYPE,
  PERMISSIONS_TAG_TYPE,
  SECURITY_PERMISSIONS_TAG_TYPE,
} from './constants';

export interface Permission {
  id: string;
  code: string;
  name: string;
  codename?: string;
  description: string;
}

export interface PermissionsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Permission[];
}

export interface SecurityPermission {
  code: string;
  has_permission: boolean;
}

export interface SecurityPermissionsResponse {
  results: SecurityPermission[];
  permissions?: string[];
}

export const rolesApi = createApi({
  reducerPath: 'rolesApi',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [ROLES_TAG_TYPE, PERMISSIONS_TAG_TYPE, SECURITY_PERMISSIONS_TAG_TYPE],
  endpoints: (builder) => ({
    getAllPermissions: builder.query<PermissionsResponse, void>({
      query: () => ({
        url: 'v1/admin/permissions/',
        method: 'GET',
        // The endpoint paginates at 50; request a large page so the full
        // catalog comes back in one call (there are ~61 permissions today).
        params: { pageSize: 200 },
      }),
      providesTags: [{ type: PERMISSIONS_TAG_TYPE, id: 'LIST' }],
    }),

    getSecurityPermissions: builder.query<
      SecurityPermissionsResponse,
      { companyId: string; userId: string }
    >({
      query: ({ companyId, userId }) => ({
        url: `v1/accounts/companies/${companyId}/users/${userId}/permissions/`,
        method: 'GET',
      }),
      providesTags: (result, error, { companyId, userId }) => [
        { type: SECURITY_PERMISSIONS_TAG_TYPE, id: `${companyId}-${userId}` },
      ],
    }),

    updateSecurityPermissions: builder.mutation<
      SecurityPermissionsResponse,
      { companyId: string; userId: string; permissions: string[] }
    >({
      query: ({ companyId, userId, permissions }) => ({
        url: `v1/accounts/companies/${companyId}/users/${userId}/permissions/`,
        method: 'POST',
        body: { permissions },
      }),
      invalidatesTags: (result, error, { companyId, userId }) => [
        { type: SECURITY_PERMISSIONS_TAG_TYPE, id: `${companyId}-${userId}` },
      ],
    }),
  }),
});

export const {
  useGetAllPermissionsQuery,
  useGetSecurityPermissionsQuery,
  useUpdateSecurityPermissionsMutation,
} = rolesApi;
